import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'cat-ecommerce-alloy';
import styles from './ProductListSortBy.module.scss';
import {
  SORT_OPTIONS,
  SORT_OPTIONS_KEYS
} from '@app/components/pages/plp/constants';
import { useAnalytics } from '@app/hooks';
import { useRef, useState } from 'react';
import { useLocation, useHistory, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import { getUrlByHashValue } from '@app/store/marketing/utils';
import { getActiveFacets } from '@app/components/pages/marketing/components/ProductCardGrid/utils';
import { useDispatch } from 'react-redux';
import { getMarketingProductDetails } from '@app/store/marketing/actions';

function ProductListSortBy({
  sortByAction,
  orderBy,
  widgetId,
  skipUrlChange = false,
  children,
  setUrlToPageOneWithFilter,
  hideCategoriesLoader = false,
  filterByEquipment,
  isMarketingPage = false,
  fbeEnabled,
  activeFacets,
  partNumbers
}) {
  const [updatedOrderBy, setupdatedOrderBy] = useState(orderBy);
  const { hash, search } = useLocation();
  const { url } = useRouteMatch();
  const history = useHistory();
  const [t] = useTranslation();
  const { dispatchWithImpressionEvent } = useAnalytics();
  const orderByRef = useRef();
  const queryParamsObject = { history, url, hash, search };
  const dispatch = useDispatch();

  const handleChange = orderByOption => {
    /*
    This is a temporal solution to an infinite loop that came with React18 and only happens on
    in responsive mode and phones

    We think this could be caused by downshift
    */
    if (orderByRef.current === orderByOption) {
      return;
    }
    orderByRef.current = orderByOption;
    const {
      filterByEquipment: filterByEquipmentUrl,
      orderBy: orderByFromUrl,
      facets: facetsFromHash
    } = queryString.parse(hash);

    dispatchWithImpressionEvent(
      sortByAction(
        widgetId
          ? {
              widgetId: widgetId,
              data: {
                [orderByFromUrl ? 'orderBy' : 'sortBy']: orderByOption,
                filterByEquipment: filterByEquipment,
                pageNumber: 1,
                batchPartNumbers: []
              }
            }
          : {
              [orderByFromUrl ? 'orderBy' : 'sortBy']: orderByOption,
              queryParamsObject
            },
        !widgetId ?? true,
        hideCategoriesLoader
      )
    );

    updatedOrderBy &&
      widgetId &&
      dispatch(getMarketingProductDetails(partNumbers, widgetId));
    const facetsArray = !Array.isArray(facetsFromHash)
      ? [facetsFromHash]
      : facetsFromHash;
    const filterByEqValue = widgetId
      ? getUrlByHashValue(filterByEquipment, widgetId, filterByEquipmentUrl)
      : filterByEquipment;
    const orderByValue = widgetId
      ? getUrlByHashValue(orderByOption, widgetId, orderByFromUrl)
      : orderByOption;
    !skipUrlChange &&
      setUrlToPageOneWithFilter &&
      setUrlToPageOneWithFilter({
        sortBy: orderByValue,
        filterByEquipment: filterByEqValue,
        fbeEnabledValue: fbeEnabled,
        isMarketingPage,
        facets: widgetId
          ? getActiveFacets(facetsArray, activeFacets, widgetId)
          : activeFacets
      });
    setupdatedOrderBy(orderByOption);
  };

  return (
    <>
      <Dropdown
        className={styles['sort-by__dropdown']}
        items={SORT_OPTIONS_KEYS}
        itemToString={option => t(SORT_OPTIONS[option])}
        selected={updatedOrderBy ? updatedOrderBy : orderBy}
        onChange={handleChange}
      />
      {children}
    </>
  );
}

ProductListSortBy.propTypes = {
  activeFacets: PropTypes.arrayOf(
    PropTypes.shape({
      facet: PropTypes.string,
      facetId: PropTypes.string,
      groupLabel: PropTypes.string,
      label: PropTypes.string,
      uom: PropTypes.string
    })
  ),
  children: PropTypes.element,
  fbeEnabled: PropTypes.bool,
  filterByEquipment: PropTypes.func,
  hideCategoriesLoader: PropTypes.bool,
  isMarketingPage: PropTypes.bool,
  orderBy: PropTypes.string,
  partNumbers: PropTypes.arrayOf(
    PropTypes.shape({
      documentNumber: PropTypes.string,
      imageURL: PropTypes.string,
      orderId: PropTypes.string,
      partName: PropTypes.string,
      partNumber: PropTypes.string,
      poNumber: PropTypes.string,
      seoUrl: PropTypes.string,
      serialNumber: PropTypes.string
    })
  ),
  setUrlToPageOneWithFilter: PropTypes.func,
  sortByAction: PropTypes.func,
  skipUrlChange: PropTypes.bool,
  widgetId: PropTypes.string
};

export default ProductListSortBy;
